/** @jsxImportSource ./simboluri-jsx */
// CARDUL «FUNDAȚIA (Pasul 17)» de pe pagina unui ax de fundație: ce a
// verificat pasul pe talpa axului — geometria adoptată (L, B, D_f, A, W),
// coeficientul de excentricitate și tasarea, fiecare combinație cu presiunile
// ei față de praguri (același tabel ca pe pagina fundației) și desenele care
// explică citirea: distribuția presiunii, tasarea pe felii, modelul Winkler
// și grinda de fundare încărcată de jos.
//
// Nota de calcul de sub el e a ARMĂRII (Pasul 18); fără cardul ăsta pagina
// axului spunea că trece sau pică la presiune fără să arate combinațiile
// din care a ieșit verdictul.

import { useT } from "../i18n";
import type { AxFundatie, RezistentaResult, StepState } from "./tipuri";
import { cheiaFundatiei, fundatiaAdoptata } from "./fundatia";
import { hashSectiune } from "./legaturi";
import { nr } from "./elemente";
import TabelCombinatiiAx from "./TabelCombinatiiAx";
import { DiagramaGrindaFundare, DiagramaPresiuni, DiagramaTasare, DiagramaWinkler }
  from "./DiagrameFundatie";

export default function CardFundatieAx({ ax, steps, id, solutie }: {
  /** numele axului („Ax 4") */
  ax: string;
  steps: Record<string, StepState>;
  id: string;
  solutie?: string | null;
}) {
  const t = useT();
  const r = fundatiaAdoptata(steps) as unknown as
    (RezistentaResult & { verificari?: { stare?: string; axe?: AxFundatie[] } }) | null;
  const rand = (r?.verificari?.axe ?? []).find((a) => a.nume === ax);
  // fără verificarea pe reacțiuni (Pasul 17 nerulat) nu e nimic de arătat:
  // predimensionarea singură n-are combinații
  if (!r || !rand) return null;
  const pasFundatie = cheiaFundatiei(steps);
  const kExc = rand.coef_excentricitate ?? 1;
  return (
    <section className="ob-card" style={{ padding: "11px 14px", marginTop: 14 }}
             data-testid="card-fundatie-ax">
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span className="ob-lbl">{t("struct.cfa.titlu")}</span>
        <span className="sg-muted" style={{ fontSize: 12 }}>
          {t("struct.cfa.sub")}
        </span>
        <span style={{ flex: 1 }} />
        <a href={hashSectiune(id, pasFundatie, solutie ?? null, { ref: ax })}
           style={{ fontSize: 12 }}>
          {t("struct.drax.pagina_fundatiei")}
        </a>
      </div>
      <div className="sg-mono" style={{ fontSize: 12, margin: "8px 0" }}
           data-testid="cfa-valori">
        {t("struct.cfa.valori", {
          l: nr(rand.L_m ?? 0, 2), b: nr(rand.B_m ?? r.latime_m ?? 0, 2),
          d: nr(r.adancime_m ?? 0, 2), a: nr(rand.A_m2 ?? 0, 2),
          wl: nr(rand.W_long_m3 ?? 0, 2), wt: nr(rand.W_transv_m3 ?? 0, 3),
        })}
        {rand.latit_local ? t("struct.drax.latit") : ""}
        {kExc !== 1 ? t("struct.cfa.kexc", { k: nr(kExc, 2) }) : ""}
        {rand.tasare_cm != null ? t("struct.drax.tasare", { n: nr(rand.tasare_cm, 1) }) : ""}
        {rand.nr_stalpi ? t("struct.cfa.stalpi", { n: rand.nr_stalpi }) : ""}
      </div>
      {kExc > 1 && (
        <div className="sg-muted" style={{ fontSize: 12, marginBottom: 6 }}>
          {t("struct.rzv.presiunile_includ_kexc", { k: nr(kExc, 2) })}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <TabelCombinatiiAx ax={rand} />
      </div>
      {/* desenele explicative, pe un rând compact: ce e μ și aria activă,
          cum se adună tasarea, ce e resortul Winkler, de ce grinda de
          fundare are semnele inversate */}
      <div className="dg-grid" style={{ marginTop: 10, gap: 12 }} data-testid="cfa-diagrame">
        {[DiagramaPresiuni, DiagramaTasare, DiagramaWinkler, DiagramaGrindaFundare]
          .map((Diagrama, i) => (
            <div key={i} style={{ flex: "1 1 220px", maxWidth: 300, fontSize: 11 }}>
              <Diagrama />
            </div>
          ))}
      </div>
    </section>
  );
}
